<template>
  <b-modal
    v-model="modalVisible"
    title="Create New Accused / Offender"
    size="lg"
    hide-footer
    centered
  >
    <vue-element-loading :active="visible" :is-full-screen="false" />
    
    <b-form @submit.prevent="submitForm">
      <b-row>
        <!-- Aadhaar Number -->
        <b-col md="6" class="mb-3">
          <b-form-group label="Aadhar Number">
            <b-form-input
              v-model="form.adharNo"
              type="text"
              placeholder="12-digit Aadhaar"
              maxlength="12"
              :state="aadhaarState"
              @input="validateAadhaar"
              @blur="validateAadhaar"
            ></b-form-input>
            <b-form-invalid-feedback :state="aadhaarState">
              <span v-if="aadhaarValidation.isInvalid">Must be exactly 12 digits.</span>
              <span v-else-if="aadhaarValidation.exists">
                Aadhaar already exists for: {{ aadhaarValidation.existingData.name }} 
                <span v-if="aadhaarValidation.existingData.alias">({{ aadhaarValidation.existingData.alias }})</span>
              </span>
            </b-form-invalid-feedback>
            <b-form-text v-if="aadhaarValidation.isLoading">
              <i class="fa fa-spinner fa-spin mr-1"></i>Checking Aadhaar...
            </b-form-text>
          </b-form-group>
        </b-col>

        <!-- Name -->
        <b-col md="6" class="mb-3">
          <b-form-group label="Full Name *">
            <b-form-input
              v-model="form.name"
              type="text"
              required
              placeholder="Enter full name"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <!-- Alias -->
        <b-col md="6" class="mb-3">
          <b-form-group label="Alias / Nickname">
            <b-form-input
              v-model="form.alias"
              type="text"
              placeholder="Alias name"
            ></b-form-input>
          </b-form-group>
        </b-col>

        <!-- Age -->
        <b-col md="6" class="mb-3">
          <b-form-group label="Age *">
            <b-form-input
              v-model.number="form.age"
              type="number"
              required
              min="1"
              max="120"
              placeholder="Age"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <!-- Gender -->
        <b-col md="6" class="mb-3">
          <b-form-group label="Gender *">
            <b-form-select
              v-model="form.sex"
              :options="genderOptions"
              required
            ></b-form-select>
          </b-form-group>
        </b-col>

        <!-- Contact Number -->
        <b-col md="6" class="mb-3">
          <b-form-group label="Contact Number">
            <b-form-input
              v-model="form.contactNo"
              type="tel"
              placeholder="Phone number"
            ></b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <!-- Address -->
      <b-form-group label="Complete Address *" class="mb-4">
        <b-form-textarea
          v-model="form.address"
          rows="3"
          required
          placeholder="Enter complete address details"
        ></b-form-textarea>
      </b-form-group>

      <!-- Actions -->
      <div class="text-right border-top pt-3">
        <b-button variant="secondary" @click="closeModal" class="mr-2">Cancel</b-button>
        <b-button variant="success" type="submit" :disabled="isSubmitDisabled">
          Save Offender & Link
        </b-button>
      </div>
    </b-form>
  </b-modal>
</template>

<script>
import axios from 'axios'
import VueElementLoading from 'vue-element-loading'

export default {
  name: 'AddOffenderModal',
  components: {
    VueElementLoading,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      visible: false,
      form: {
        adharNo: '',
        name: '',
        alias: '',
        age: '',
        sex: '',
        address: '',
        contactNo: '',
      },
      genderOptions: [
        { value: '', text: 'Select Gender' },
        { value: 'Male', text: 'Male' },
        { value: 'Female', text: 'Female' },
        { value: 'Other', text: 'Other' },
      ],
      aadhaarValidation: {
        isLoading: false,
        isValid: false,
        isInvalid: false,
        exists: false,
        existingData: null,
      },
      aadhaarValidationTimeout: null,
    }
  },
  computed: {
    modalVisible: {
      get() {
        return this.value
      },
      set(val) {
        this.$emit('input', val)
      }
    },
    aadhaarState() {
      if (!this.form.adharNo) return null
      if (this.aadhaarValidation.isInvalid || this.aadhaarValidation.exists) return false
      if (this.aadhaarValidation.isValid && !this.aadhaarValidation.exists) return true
      return null
    },
    isSubmitDisabled() {
      // Disable if Aadhaar validation is in progress or failed
      if (this.form.adharNo) {
        if (this.aadhaarValidation.isLoading || this.aadhaarValidation.isInvalid || this.aadhaarValidation.exists) {
          return true
        }
      }
      return this.visible
    },
  },
  methods: {
    closeModal() {
      this.$emit('input', false)
      this.resetForm()
    },
    resetForm() {
      this.form = {
        adharNo: '',
        name: '',
        alias: '',
        age: '',
        sex: '',
        address: '',
        contactNo: '',
      }
      this.aadhaarValidation = {
        isLoading: false,
        isValid: false,
        isInvalid: false,
        exists: false,
        existingData: null,
      }
    },
    validateAadhaar(event) {
      const isBlur = event && event.type === 'blur'
      let adharNo = this.form.adharNo ? this.form.adharNo.toString().trim() : ''
      this.form.adharNo = adharNo

      if (this.aadhaarValidationTimeout) {
        clearTimeout(this.aadhaarValidationTimeout)
      }

      this.aadhaarValidation = {
        isLoading: false,
        isValid: false,
        isInvalid: false,
        exists: false,
        existingData: null,
      }

      if (!adharNo) return

      if (!/^\d+$/.test(adharNo)) {
        this.aadhaarValidation.isInvalid = true
        return
      }

      if (adharNo.length < 12) {
        if (isBlur) {
          this.aadhaarValidation.isInvalid = true
        }
        return
      }

      if (adharNo.length === 12) {
        this.aadhaarValidation.isLoading = true
        this.aadhaarValidationTimeout = setTimeout(async () => {
          try {
            const response = await axios.get(`/habitual/check-aadhaar/${adharNo}`)
            if (response.data.status) {
              this.aadhaarValidation.isValid = true
              this.aadhaarValidation.exists = response.data.exists
              if (response.data.exists && response.data.data) {
                this.aadhaarValidation.existingData = response.data.data
              }
            } else {
              this.aadhaarValidation.isInvalid = true
            }
          } catch (err) {
            console.error('Error validating Aadhaar:', err)
          } finally {
            this.aadhaarValidation.isLoading = false
          }
        }, 500)
      }
    },
    async submitForm() {
      this.visible = true
      try {
        const formData = new FormData()
        Object.keys(this.form).forEach(key => {
          formData.append(key, this.form[key])
        })

        // Also add networks, phone list and family structures to match standard add route parses
        formData.append('phone', JSON.stringify([{ mobile: this.form.contactNo || '' }]))
        formData.append('networks', JSON.stringify([]))
        formData.append('familyDetails', JSON.stringify({}))
        formData.append('vehicleOwned', JSON.stringify([]))
        formData.append('gun', JSON.stringify([]))
        formData.append('prominentIdentificationMarks', JSON.stringify([]))
        formData.append('expertise', JSON.stringify([]))
        formData.append('isHabitual', 'false')

        const response = await axios.post('/habitual/add', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            'token': localStorage.getItem('token'),
          },
        })

        if (response.data.status) {
          const savedOffender = response.data.data
          // Format offender to match dropdown selection schemas: { _id, name, age, alias, displayName }
          const name = savedOffender.name || 'Unknown'
          const alias = savedOffender.alias ? ` (${savedOffender.alias})` : ''
          savedOffender.displayName = name + alias
          
          this.$dlg.alert('Offender created successfully!', () => {
            this.$emit('saved', savedOffender)
            this.closeModal()
          }, { messageType: 'success' })
        } else {
          this.$dlg.alert(response.data.message || 'Failed to create offender', () => {}, { messageType: 'error' })
        }
      } catch (error) {
        console.error('Error submitting form:', error)
        this.$dlg.alert('Failed to save offender record.', () => {}, { messageType: 'error' })
      } finally {
        this.visible = false
      }
    },
  },
}
</script>
